import { useCallback, useEffect, useRef, useState } from "react";
import { X, Volume2, VolumeX, ArrowRight, Megaphone, Loader2 } from "lucide-react";
import { getActiveAnnouncements } from "@/lib/api";
import { resolveUrl } from "@/lib/media";

// Annonces immersives : Mode A (vidéo de lancement plein écran, son coupé + bouton) et
// Mode B (notification flottante bas-droite, style GTA). File d'attente unique : jamais
// deux annonces en même temps. Répétition configurable par annonce (délai d'apparition,
// durée visible, réapparition, masquage après fermeture). Les timers sont mis en pause
// quand l'onglet n'est pas visible.

const seenKey = (id) => `ks_ann_seen_${id}`;
const isBot = () => typeof navigator !== "undefined" && /bot|crawl|spider|slurp|googlebot|bingpreview|duckduckbot|baiduspider|yandex|facebookexternalhit|lighthouse|headless|prerender/i.test(navigator.userAgent || "");
// Contournement test/QA : localStorage 'ks_ann_force' = '1' force l'affichage même en headless.
const forceShow = () => { try { return localStorage.getItem("ks_ann_force") === "1"; } catch { return false; } };
const ytId = (u = "") => { const m = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/); return m ? m[1] : null };

const previewParams = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    return { active: sp.get("annPreview") === "1", id: sp.get("annId") || "" };
  } catch { return { active: false, id: "" }; }
};

// Nombre d'apparitions déjà effectuées pour cette visite (session).
const showsCount = (id) => { try { return Number(sessionStorage.getItem(`ks_ann_shows_${id}`) || 0); } catch { return 0; } };
const incShows = (id) => { try { sessionStorage.setItem(`ks_ann_shows_${id}`, String(showsCount(id) + 1)); } catch { /* noop */ } };

// Fermeture volontaire (croix) ou clic sur l'annonce : mémorisation DÉFINITIVE pour ce visiteur.
// La valeur "perm" = l'annonce ne réapparaît jamais, même après actualisation ou retour plus tard.
// (hideAfterCloseHours est conservé dans le modèle admin mais n'est plus utilisé pour la fermeture
// volontaire : elle est toujours définitive. Il reste utile uniquement si on voulait un masquage
// temporaire — retiré de la fermeture volontaire pour respecter la demande.)
const rememberClosed = (a) => {
  try { localStorage.setItem(seenKey(a.id), "perm"); } catch { /* noop */ }
};
const isClosed = (a) => {
  try {
    const v = localStorage.getItem(seenKey(a.id));
    if (!v) return false;
    if (v === "perm") return true;
    const until = Number(v);
    if (!Number.isFinite(until)) return false; // ancien format (timestamp) -> traité comme expiré
    return until > Date.now();
  } catch { return false; }
};

const Cta = ({ a, onClick }) => a.ctaLabel && a.ctaUrl ? (
  <a href={a.ctaUrl} data-testid="announcement-cta" onClick={onClick}
    className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-electric text-black text-[11px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors">
    {a.ctaLabel} <ArrowRight size={14} />
  </a>
) : null;

// Détecte l'orientation de l'écran : téléphone (portrait) vs ordinateur (paysage).
const usePortrait = () => {
  const [p, setP] = useState(typeof window !== "undefined" ? window.innerWidth < window.innerHeight : false);
  useEffect(() => {
    const h = () => setP(window.innerWidth < window.innerHeight);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return p;
};

// Détecte la préférence « réduire les animations » du système.
const prefersReducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Minuterie mise en pause quand l'onglet n'est pas visible.
const usePageVisibleTimer = (callback, delaySec, active) => {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  useEffect(() => {
    if (!active || !delaySec || delaySec <= 0) return;
    let remaining = delaySec * 1000;
    let started = Date.now();
    let t = null;
    const fire = () => cbRef.current();
    const schedule = () => { clearTimeout(t); started = Date.now(); t = setTimeout(fire, remaining); };
    const onVis = () => {
      if (document.hidden) { clearTimeout(t); remaining -= (Date.now() - started); }
      else schedule();
    };
    schedule();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", onVis); };
  }, [delaySec, active]);
};

// ---- Mode A : vidéo de lancement plein écran ----
// Deux fichiers possibles : videoUrl (ordinateur 16:9) + videoUrlMobile (téléphone 9:16).
// Le titre est INTERNE (admin) — seul le message s'affiche sur le site public.
const VideoAnnouncement = ({ a, onClose, preview }) => {
  const vidRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const portrait = usePortrait();
  // Choix du fichier adapté à l'écran ; repli propre sur l'autre si une seule version.
  const src = (portrait && a.videoUrlMobile) ? a.videoUrlMobile : (a.videoUrl || a.videoUrlMobile);
  const yt = ytId(src);
  const toggleSound = () => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.volume = 1;
    setMuted(v.muted);
  };
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Certains navigateurs ne démarrent pas le téléchargement avec autoPlay seul :
  // on force load() puis play() (muet, donc autorisé). Garantit le démarrage.
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    try { v.load(); } catch { /* noop */ }
    const t = setTimeout(() => { v.play().catch(() => {}); }, 50);
    return () => clearTimeout(t);
  }, [src]);
  // Sur téléphone avec une vidéo verticale : plein écran adapté (cover) ;
  // sinon contain si la vidéo ne correspond pas à l'orientation (pas de recadrage agressif).
  const mismatch = portrait ? !a.videoUrlMobile : !a.videoUrl;
  return (
    <div className="fixed inset-0 z-[9997] bg-black" data-testid="announcement-video">
      {yt ? (
        <iframe title={a.title || "Annonce"}
          src={`https://www.youtube.com/embed/${yt}?autoplay=1&mute=1&rel=0&playsinline=1`}
          className="w-full h-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
      ) : (
        <video ref={vidRef} key={src} src={resolveUrl(src)} poster={a.image ? resolveUrl(a.image) : undefined}
          autoPlay muted loop playsInline preload="metadata"
          onCanPlay={() => setLoading(false)} onError={() => { setLoading(false); setFailed(true); }}
          className={`w-full h-full ${mismatch ? "object-contain" : "object-cover"}`} data-testid="announcement-video-el" />
      )}
      {loading && !yt && !failed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="announcement-video-loading">
          <span className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-black/70 backdrop-blur px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase text-white/80">
            <Loader2 size={15} className="animate-spin text-electric" /> Chargement de la vidéo…
          </span>
        </div>
      )}
      {failed && !yt && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="announcement-video-error">
          <div className="text-center">
            <p className="text-sm text-white/70">La vidéo n'a pas pu être chargée.</p>
            <button onClick={onClose} className="pointer-events-auto mt-3 text-[11px] tracking-[0.15em] uppercase text-electric hover:text-white">Continuer vers le site</button>
          </div>
        </div>
      )}
      {/* Seul le message s'affiche — le titre est réservé à l'identification dans l'admin */}
      {(a.message || a.ctaLabel) && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-6 sm:p-10 pointer-events-none">
          <div className="max-w-3xl pointer-events-auto">
            {a.message && <p className="text-sm sm:text-base text-white/90 whitespace-pre-line" data-testid="announcement-message">{a.message}</p>}
            <Cta a={a} />
          </div>
        </div>
      )}
      {preview && (
        <span className="absolute top-4 left-4 rounded-full border border-electric/40 bg-black/70 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase text-electric" data-testid="announcement-preview-badge">
          Aperçu admin — non comptabilisé
        </span>
      )}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {!yt && (
          <button onClick={toggleSound} data-testid="announcement-sound-toggle"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/60 backdrop-blur px-4 py-2 text-[11px] tracking-[0.12em] uppercase text-white hover:border-electric hover:text-electric transition-colors">
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />} {muted ? "Activer le son" : "Couper le son"}
          </button>
        )}
        <button onClick={onClose} data-testid="announcement-close" aria-label="Fermer l'annonce"
          className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/25 bg-black/60 backdrop-blur text-white hover:border-electric hover:text-electric transition-colors">
          <X size={17} />
        </button>
      </div>
    </div>
  );
};

// ---- Mode B : notification flottante (panneau glissant, position configurable) ----
// Position : bas-droite (défaut), bas-gauche, haut-droite, haut-gauche.
// Sur mobile : la carte prend toute la largeur avec marges, sans masquer la navigation (bas) ni le menu (haut).
const POSITIONS = {
  br: { cls: "bottom-20 right-4 sm:bottom-6 sm:right-6", axis: "X", border: "border-l-4" },
  bl: { cls: "bottom-20 left-4 sm:bottom-6 sm:left-6", axis: "X", border: "border-l-4" },
  tr: { cls: "top-20 right-4 sm:top-6 sm:right-6", axis: "X", border: "border-l-4" },
  tl: { cls: "top-20 left-4 sm:top-6 sm:left-6", axis: "X", border: "border-l-4" },
};

const PopupAnnouncement = ({ a, onClose, onAutoHide, onCtaClick, preview }) => {
  const [leaving, setLeaving] = useState(false);
  const leaveTimer = useRef(null);
  const reduced = prefersReducedMotion();
  const pos = POSITIONS[a.position] || POSITIONS.br;
  const dir = (a.position || "br").endsWith("l") ? "-110%" : "110%";
  // Fermeture volontaire (croix) → onClose (mémorisation définitive)
  const requestClose = useCallback(() => {
    if (reduced) { onClose(); return; }
    setLeaving(true);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(onClose, 320);
  }, [onClose, reduced]);
  // Disparition automatique (délai écoulé, sans action) → onAutoHide (peut réapparaître)
  const requestAutoHide = useCallback(() => {
    if (reduced) { onAutoHide(); return; }
    setLeaving(true);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(onAutoHide, 320);
  }, [onAutoHide, reduced]);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  // Disparition automatique (pausée quand l'onglet est masqué).
  usePageVisibleTimer(requestAutoHide, a.autoHideSec, !!a.autoHideSec);
  return (
    <div className="fixed inset-0 z-[9996] pointer-events-none" data-testid="announcement-popup-wrap">
      <style>{`@keyframes ksAnnIn{from{transform:translate${pos.axis}(${dir});opacity:0}to{transform:translate${pos.axis}(0);opacity:1}}@keyframes ksAnnOut{from{transform:translate${pos.axis}(0);opacity:1}to{transform:translate${pos.axis}(${dir});opacity:0}}`}</style>
      <div data-testid="announcement-popup" data-state={leaving ? "leaving" : "entering"} data-position={a.position || "br"}
        className={`pointer-events-auto absolute ${pos.cls} w-[calc(100vw-2rem)] max-w-sm rounded-xl overflow-hidden ${pos.border} border-electric bg-[#0a0a0a]/95 backdrop-blur-xl shadow-2xl shadow-black/60`}
        style={reduced ? undefined : { animation: `${leaving ? "ksAnnOut" : "ksAnnIn"} 0.4s cubic-bezier(0.22,1,0.36,1) both` }}>
        <div className="flex items-center justify-between px-4 pt-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-electric"><Megaphone size={12} /> Annonce</span>
          <button onClick={requestClose} data-testid="announcement-close" aria-label="Fermer l'annonce"
            className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-white/15 bg-black/50 text-white/70 hover:text-white hover:border-electric transition-colors"><X size={15} /></button>
        </div>
        {preview && (
          <span className="mx-4 mt-2 inline-block rounded-full border border-electric/40 bg-black/70 px-3 py-1 text-[10px] tracking-[0.15em] uppercase text-electric" data-testid="announcement-preview-badge">
            Aperçu admin — non comptabilisé
          </span>
        )}
        {a.image && (
          <div className={`mt-3 overflow-hidden ${a.imageFormat === "vertical" ? "aspect-[3/4] max-h-72 mx-auto" : a.imageFormat === "square" ? "aspect-square max-h-64 mx-auto" : "h-36"}`}>
            <img src={resolveUrl(a.image)} alt="" className="w-full h-full object-cover" data-testid="announcement-image" />
          </div>
        )}
        <div className="p-4">
          {a.title && <h3 className="font-serif text-xl text-white" data-testid="announcement-title">{a.title}</h3>}
          {a.message && <p className="mt-1.5 text-sm text-white/75 leading-relaxed whitespace-pre-line line-clamp-5">{a.message}</p>}
          <Cta a={a} onClick={onCtaClick} />
        </div>
      </div>
    </div>
  );
};

export const ImmersiveAnnouncements = () => {
  const { active: preview } = previewParams();
  const [queue, setQueue] = useState([]); // annonces affichables MAINTENANT
  const [pending, setPending] = useState([]); // annonces en attente (délai / réapparition)
  const current = queue[0] || null;
  const shownRef = useRef(0); // annonces différentes montrées pendant cette visite

  // Sélection d'une annonce affichable : fenêtre de fermeture échue + quota par visite.
  const eligible = useCallback((a) => {
    if (!a) return false;
    if (!preview) {
      if (isClosed(a)) return false;
      if (a.maxShowsPerSession > 0 && showsCount(a.id) >= a.maxShowsPerSession) return false;
      if (shownRef.current >= 2 && !queue.some((q) => q.id === a.id)) return false; // max 2 annonces différentes par visite
    }
    return true;
  }, [preview, queue]);

  // Chargement initial : tri par priorité (déjà fait côté API) + planification.
  useEffect(() => {
    if (isBot() && !forceShow() && !preview) return;
    let alive = true;
    (async () => {
      try {
        let list = [];
        if (preview) {
          // Aperçu admin : annonce transmise en sessionStorage par le bouton « Aperçu »,
          // jamais sauvegardée. Affichage immédiat, non comptabilisé.
          try {
            const raw = sessionStorage.getItem("ks_ann_preview_payload");
            if (raw) {
              const a = JSON.parse(raw);
              if (a && typeof a === "object") list = [{ ...a, id: a.id || "preview", enabled: true, trigger: "immediate", displayDelaySec: 0 }];
            }
          } catch { list = []; }
        } else {
          list = (await getActiveAnnouncements()) || [];
        }
        if (!alive) return;
        // Déclenchement calculé UNE fois, à l'arrivée sur le site (le composant reste
        // monté entre les pages internes : le délai ne redémarre pas en naviguant).
        const ready = [], later = [];
        for (const a of list) {
          const delay = a.mode === "popup" && a.trigger === "delayed" ? Number(a.displayDelaySec || 0) : 0;
          if (delay > 0) later.push({ a, at: Date.now() + delay * 1000 });
          else ready.push(a);
        }
        setQueue(ready);
        setPending(later);
      } catch { /* silencieux : le site fonctionne sans annonces */ }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Promotion des annonces en attente quand leur heure est venue.
  useEffect(() => {
    if (pending.length === 0) return;
    const t = setInterval(() => {
      const now = Date.now();
      setPending((p) => {
        const due = p.filter((x) => x.at <= now);
        if (due.length === 0) return p;
        setQueue((q) => (q.length > 0 ? q : due.filter((x) => eligible(x.a)).map((x) => x.a).slice(0, 1)));
        return p.filter((x) => x.at > now);
      });
    }, 1000);
    return () => clearInterval(t);
  }, [pending.length, eligible]);

  // Fermeture VOLONTAIRE (croix ou clic) : mémorisation définitive + passage à la suivante.
  const closeCurrent = useCallback(() => {
    const a = current;
    if (!a) return;
    if (!preview) {
      incShows(a.id);
      rememberClosed(a); // fermeture volontaire / clic : l'annonce ne réapparaît JAMAIS pour ce visiteur
      shownRef.current += 1;
    }
    setQueue((q) => q.slice(1));
  }, [current, preview]);

  // Disparition AUTOMATIQUE (délai écoulé, sans action du visiteur) : pas de mémorisation
  // définitive — l'annonce peut réapparaître selon la fréquence configurée (reappearDelayMin).
  const autoHideCurrent = useCallback(() => {
    const a = current;
    if (!a) return;
    if (!preview) {
      incShows(a.id);
      const canReappear = a.reappearDelayMin > 0 && !(a.maxShowsPerSession > 0 && showsCount(a.id) >= a.maxShowsPerSession);
      if (canReappear && shownRef.current < 2) {
        setPending((p) => [...p, { a, at: Date.now() + Number(a.reappearDelayMin) * 60000 }]);
      } else {
        shownRef.current += 1; // annonce terminée pour cette visite (quota atteint / pas de réapparition)
      }
    }
    setQueue((q) => q.slice(1));
  }, [current, preview]);

  if (!current || !eligible(current)) {
    return null;
  }
  // Clic sur l'annonce = consultation : mémorisation définitive (comme fermeture volontaire).
  const onCtaClick = () => { if (!preview) rememberClosed(current); };
  return current.mode === "video"
    ? <VideoAnnouncement a={current} onClose={closeCurrent} preview={preview} />
    : <PopupAnnouncement a={current} onClose={closeCurrent} onAutoHide={autoHideCurrent} preview={preview} onCtaClick={onCtaClick} />;
};
